import { Inject, Injectable, TemplateRef } from '@angular/core';
import { MatDrawerMode } from '@angular/material/sidenav';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { GeneralRemoteControllerService } from 'core/api';
import { SS_VERSION } from 'core/config';
import { mapKey, subjectFromObs } from 'core/rx-js';
import { ScreenUtilsService } from 'core/services';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map, startWith, take } from 'rxjs';
import { JwtService } from '../../global-services/jwt.service';

@Injectable({ providedIn: 'root' })
export class SidenavService {

  readonly mobileToolbarTemplateRefSubject = new BehaviorSubject<TemplateRef<any>>(null);
  readonly userMenuOpenedSubject = new BehaviorSubject<boolean>(false);
  readonly expandedSubject = new BehaviorSubject<boolean>(true)
  get expanded() {
    return this.expandedSubject.value;
  }
  readonly EXPANDED_WIDTH = 240;
  readonly COLLAPSED_WIDTH = 45;


  readonly rotateObs = this.expandedSubject.pipe(
    map((expanded) => expanded ? '180deg' : '0deg')
  )


  readonly openedSubject = subjectFromObs({
    observable: combineLatest([this._jwtService.refreshTokenExistsSubject, this._screenUtilsService.isSmallerScreenObs, this.expandedSubject]).pipe(
      map(([refreshTokenExist, isSmallerScreen, expanded]) => refreshTokenExist && (!isSmallerScreen || expanded)),
      distinctUntilChanged()
    ),
    initialValue: false
  })

  readonly showMobileToolbarObs = combineLatest([this._jwtService.refreshTokenExistsSubject, this._screenUtilsService.isSmallerScreenObs, this.expandedSubject]).pipe(
    map(([refreshTokenExists, isSmallerScreen, expanded]) => refreshTokenExists && isSmallerScreen && true),
    distinctUntilChanged()
  )

  readonly routeSubject = subjectFromObs({
    initialValue: this._router.url,
    observable: this._router.events.pipe(
      startWith(this._router.url),
      filter((event) => event instanceof NavigationEnd),
      map((event) => event as NavigationEnd),
      map((navigationEndEvent) => navigationEndEvent.url),
    )
  })

  readonly routeChangeStartObs = this._router.events.pipe(
    startWith(this._router.url),
    filter((event) => event instanceof NavigationStart)
  )

  readonly modeSubject = subjectFromObs<MatDrawerMode>({
    observable: this._screenUtilsService.isSmallerScreenObs.pipe(
      map((isSmallScreen): MatDrawerMode => isSmallScreen ? 'over' : 'side')
    ),
    initialValue: 'over'
  })

  readonly screenSizeSubject = this._screenUtilsService.screenSizeSubject;

  readonly widthObs = combineLatest([this.expandedSubject, this.modeSubject]).pipe(
    map(([expanded, mode]) => {
      if (expanded) {
        if (mode === 'over') {
          return '100%';
        }

        return this.EXPANDED_WIDTH / 10 + 'rem';
      }

      return this.COLLAPSED_WIDTH / 10 + 'rem'
    })
  );



  readonly isSmallScreenSubject = subjectFromObs({
    initialValue: false,
    observable: this.screenSizeSubject.pipe(
      map((size) => size === 'xs' || size === 'sm'),
      distinctUntilChanged()
    ),

  })

  readonly marginLeftObs = combineLatest([
    this.widthObs,
    this.openedSubject,
  ])
    .pipe(
      map(([width, opened]) => !opened ? '0px' : width),
      distinctUntilChanged()
    )

  readonly contentWidthObs = combineLatest([
    this.widthObs,
    this.openedSubject,
  ])
    .pipe(
      map(([width, opened]) => !opened ? '100%' : 'calc(100vw - ' + width + ')'),
      distinctUntilChanged()
    )



  get feVersion() {
    return this._version;
  }

  readonly beVersionSubject = subjectFromObs({
    initialValue: null,
    observable: this._generalRemoteControllerService.version().pipe(
      take(1),
      mapKey('version')
    )
  })

  readonly versionDisplayObs = this.beVersionSubject.pipe(
    map(beVersion => ['UI:' + this.feVersion, beVersion && ('API:' + beVersion)].filter(Boolean).join(', '))
  )


  constructor(
    private readonly _jwtService: JwtService,
    private readonly _screenUtilsService: ScreenUtilsService,
    private readonly _router: Router,
    private readonly _generalRemoteControllerService: GeneralRemoteControllerService,
    @Inject(SS_VERSION)
    private readonly _version: string
  ) {


    this.isSmallScreenSubject
      .pipe(filter(Boolean))
      .subscribe(() => {
        this.expandedSubject.next(false);
      })

    // this.routeChangeStartObs.subscribe(() => {
    //   this.mobileToolbarTemplateRefSubject.next(null);
    // })
  }

  handleReroute() {
    if (this.isSmallScreenSubject.value) {
      this.expandedSubject.next(false)
    }
  }

  toggleExpanded() {
    const prev = this.expanded;
    this.expandedSubject.next(!prev);
  }
}
